import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getPortalCompany,
  getPortalMetrics,
  getPortalContacts,
  createPortalContact,
  updatePortalContact,
  deletePortalContact,
  getPortalFundInvestments,
  getPortalCompanyTransactions,
  getPortalUsers,
  getPortalInvitations,
  getAllPortalInvitations,
  changePortalUserRole,
  changePortalUserAccess,
  changePortalUserOgsmAccess,
  removePortalUser,
  invitePortalUser,
  revokePortalInvitation,
  resendPortalInvitation,
  type InviteUserRequest,
  type OgsmAccess,
  type PortalUsersResponse,
  type GetPortalCompanyTransactionsParams,
  type CreatePortalContactRequest,
  type UpdatePortalContactRequest,
} from '@/services/portal.service'
import type { AllowedApp } from '@/types/api'
import { adminKeys } from '@/hooks/query-keys'

export const portalKeys = {
  all: ['portal'] as const,
  company: () => [...portalKeys.all, 'company'] as const,
  metrics: (periodType?: string) =>
    [...portalKeys.all, 'metrics', periodType] as const,
  metricsSeries: (params: Record<string, unknown>) =>
    [...portalKeys.all, 'metrics-series', params] as const,
  metricLabels: () => [...portalKeys.all, 'metric-labels'] as const,
  financials: (params: Record<string, unknown>) =>
    [...portalKeys.all, 'financials', params] as const,
  fundInvestments: () => [...portalKeys.all, 'fund-investments'] as const,
  transactions: (params: GetPortalCompanyTransactionsParams) =>
    [...portalKeys.all, 'transactions', params] as const,
  contacts: () => [...portalKeys.all, 'contacts'] as const,
  users: () => [...portalKeys.all, 'users'] as const,
  invitations: () => [...portalKeys.all, 'invitations'] as const,
  companyUsers: (companyId: string) =>
    [...portalKeys.all, 'company-users', companyId] as const,
  companyInvitations: (companyId: string) =>
    [...portalKeys.all, 'company-invitations', companyId] as const,
  allInvitations: () => [...portalKeys.all, 'all-invitations'] as const,
}

export function usePortalCompany() {
  return useQuery({
    queryKey: portalKeys.company(),
    queryFn: getPortalCompany,
  })
}

export function usePortalMetrics(periodType?: string) {
  return useQuery({
    queryKey: portalKeys.metrics(periodType),
    queryFn: () => getPortalMetrics(periodType),
  })
}

export function usePortalContacts() {
  return useQuery({
    queryKey: portalKeys.contacts(),
    queryFn: getPortalContacts,
  })
}

export function useCreatePortalContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreatePortalContactRequest) => createPortalContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.contacts() })
    },
  })
}

export function useUpdatePortalContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      data,
    }: {
      contactId: string
      data: UpdatePortalContactRequest
    }) => updatePortalContact(contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.contacts() })
    },
  })
}

export function useDeletePortalContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) => deletePortalContact(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.contacts() })
    },
  })
}

export function usePortalFundInvestments() {
  return useQuery({
    queryKey: portalKeys.fundInvestments(),
    queryFn: getPortalFundInvestments,
    staleTime: 5 * 60 * 1000,
  })
}

export function usePortalCompanyTransactions(
  params: GetPortalCompanyTransactionsParams = {}
) {
  return useQuery({
    queryKey: portalKeys.transactions(params),
    queryFn: () => getPortalCompanyTransactions(params),
    staleTime: 5 * 60 * 1000,
  })
}

export function usePortalUsers() {
  return useQuery({
    queryKey: portalKeys.users(),
    queryFn: () => getPortalUsers(),
    staleTime: 2 * 60 * 1000,
  })
}

export function useChangePortalUserRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      changePortalUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.users() })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useChangePortalUserAccess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      allowedApps,
    }: {
      userId: string
      allowedApps: AllowedApp[]
    }) => changePortalUserAccess(userId, allowedApps),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.users() })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useChangePortalUserOgsmAccess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      ogsmAccess,
    }: {
      userId: string
      ogsmAccess: OgsmAccess
    }) => changePortalUserOgsmAccess(userId, ogsmAccess),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.users() })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useRemovePortalUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => removePortalUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.users() })
    },
  })
}

export function usePortalInvitations() {
  return useQuery({
    queryKey: portalKeys.invitations(),
    queryFn: () => getPortalInvitations(),
    staleTime: 2 * 60 * 1000,
  })
}

export function useInvitePortalUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: InviteUserRequest) => invitePortalUser(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: portalKeys.users() })
      queryClient.invalidateQueries({ queryKey: portalKeys.invitations() })
      queryClient.invalidateQueries({ queryKey: portalKeys.allInvitations() })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
      if (variables.companyId) {
        queryClient.invalidateQueries({
          queryKey: portalKeys.companyUsers(variables.companyId),
        })
        queryClient.invalidateQueries({
          queryKey: portalKeys.companyInvitations(variables.companyId),
        })
      }
    },
  })
}

export function useRevokePortalInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invitationId: string) => revokePortalInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.invitations() })
    },
  })
}

// =============================================================================
// Company-scoped hooks (for admin Portco Access tab)
// =============================================================================

export function useCompanyPortalUsers(companyId: string, enabled = true) {
  return useQuery({
    queryKey: portalKeys.companyUsers(companyId),
    queryFn: () => getPortalUsers(companyId),
    staleTime: 2 * 60 * 1000,
    enabled: enabled && Boolean(companyId),
  })
}

export function useAllPortalInvitations() {
  return useQuery({
    queryKey: portalKeys.allInvitations(),
    queryFn: getAllPortalInvitations,
    staleTime: 2 * 60 * 1000,
  })
}

export function useCompanyInvitePortalUser(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<InviteUserRequest, 'companyId'>) =>
      invitePortalUser({ ...data, companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: portalKeys.companyUsers(companyId),
      })
      queryClient.invalidateQueries({
        queryKey: portalKeys.companyInvitations(companyId),
      })
      queryClient.invalidateQueries({
        queryKey: portalKeys.allInvitations(),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useCompanyChangePortalUserRole(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      changePortalUserRole(userId, role, companyId),
    onMutate: async (variables) => {
      const key = portalKeys.companyUsers(companyId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PortalUsersResponse>(key)
      queryClient.setQueryData<PortalUsersResponse>(key, (old) =>
        old
          ? {
              ...old,
              data: old.data.map((u) =>
                u.id === variables.userId ? { ...u, role: variables.role } : u
              ),
            }
          : old
      )
      return { previous, key }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: portalKeys.companyUsers(companyId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useCompanyChangePortalUserAccess(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      allowedApps,
    }: {
      userId: string
      allowedApps: AllowedApp[]
    }) => changePortalUserAccess(userId, allowedApps, companyId),
    onMutate: async (variables) => {
      const key = portalKeys.companyUsers(companyId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PortalUsersResponse>(key)
      queryClient.setQueryData<PortalUsersResponse>(key, (old) =>
        old
          ? {
              ...old,
              data: old.data.map((u) =>
                u.id === variables.userId
                  ? {
                      ...u,
                      allowedApps: variables.allowedApps,
                    }
                  : u
              ),
            }
          : old
      )
      return { previous, key }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: portalKeys.companyUsers(companyId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useCompanyChangePortalUserOgsmAccess(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      ogsmAccess,
    }: {
      userId: string
      ogsmAccess: OgsmAccess
    }) => changePortalUserOgsmAccess(userId, ogsmAccess, companyId),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: portalKeys.companyUsers(companyId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useCompanyRemovePortalUser(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => removePortalUser(userId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: portalKeys.companyUsers(companyId),
      })
      queryClient.invalidateQueries({ queryKey: adminKeys.users() })
    },
  })
}

export function useCompanyRevokePortalInvitation(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      revokePortalInvitation(invitationId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: portalKeys.companyInvitations(companyId),
      })
      queryClient.invalidateQueries({
        queryKey: portalKeys.allInvitations(),
      })
    },
  })
}

export function useCompanyResendPortalInvitation(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      resendPortalInvitation(invitationId, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: portalKeys.companyInvitations(companyId),
      })
      queryClient.invalidateQueries({
        queryKey: portalKeys.allInvitations(),
      })
    },
  })
}
